import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  MenuItem,
  Box,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import makeStyles from '@mui/styles/makeStyles';
import EditItemView from '../common/components/EditItemView';
import { useTranslation } from '../common/components/LocalizationProvider';
import { getWithExpiry } from '../common/util/localstorage';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  clientForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid #ccc',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
}));

const ProductPage = () => {
  const classes = useStyles();
  const t = useTranslation();

  const [item, setItem] = useState();
  const [clients, setClients] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    username: '',
    email: '',
    phone: '',
    adresse: '',
  });

  // Charger clients existants
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_URL_NAME}/clients`, {
          headers: {
            Authorization: `Bearer ${getWithExpiry('TOKEN')}`,
          },
        });
        if (res.ok) {
          setClients(await res.json());
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchClients();
  }, []);

  const validate = () =>
    item &&
    item.reference &&
    item.designation &&
    item.marque &&
    item.model &&
    item.type &&
    item.client;

  const handleCreateClient = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_URL_NAME}/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getWithExpiry('TOKEN')}`,
        },
        body: JSON.stringify(newClient),
      });
      if (res.ok) {
        const createdClient = await res.json();
        setClients([...clients, createdClient]);
        setItem({ ...item, client: createdClient._id });
        setNewClient({ username: '', email: '', phone: '', adresse: '' });
        setShowClientForm(false);
      } else {
        console.error(await res.text());
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <EditItemView endpoint="products" item={item} setItem={setItem} validate={validate}>
      {item && (
        <>
          {/* Section Produit */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              
              {/* Champs du produit */}
              <TextField
                label="Référence"
                value={item.reference || ''}
                onChange={(e) => setItem({ ...item, reference: e.target.value })}
              />
              <TextField
                label="Désignation"
                value={item.designation || ''}
                onChange={(e) => setItem({ ...item, designation: e.target.value })}
              />
              <TextField
                label="Marque"
                value={item.marque || ''}
                onChange={(e) => setItem({ ...item, marque: e.target.value })}
              />
              <TextField
                label="Modèle"
                value={item.model || ''}
                onChange={(e) => setItem({ ...item, model: e.target.value })}
              />

              {/* Type de maintenance */}
              <TextField
                select
                label="Type de maintenance"
                value={item.type || ''}
                onChange={(e) => setItem({ ...item, type: e.target.value })}
              >
                {[
                  'Vente',
                  'Électronique',
                  'Mécanique',
                  'Électro-Mécanique',
                  'Électrique',
                  'Hydraulique',
                  'Pneumatique',
                  'Carrosserie',
                  'Peinture',
                  'Diagnostic électronique',
                  'Entretien général',
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              {/* ✅ Champ Statut */}
              <TextField
                select
                label="Statut"
                value={item.statut || 'en attente'}
                onChange={(e) => setItem({ ...item, statut: e.target.value })}
              >
                {['en attente','partiel', 'livré', 'retour','annuler'].map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Commentaire"
                value={item.commentaire || ''}
                onChange={(e) => setItem({ ...item, commentaire: e.target.value })}
              />

              {/* Sélection client */}
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  select
                  label="Client"
                  value={item.client?._id || item.client || ''}
                  onChange={(e) => setItem({ ...item, client: e.target.value })}
                  style={{ flex: 1 }}
                >
                  {Array.isArray(clients) &&
                    clients.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.username}
                      </MenuItem>
                    ))}
                </TextField>
                <IconButton onClick={() => setShowClientForm(!showClientForm)} size="large">
                  <AddIcon />
                </IconButton>
              </Box>

              {/* Formulaire pour créer un nouveau client */}
              {showClientForm && (
                <Box className={classes.clientForm}>
                  <Typography variant="subtitle2">Créer un nouveau client</Typography>
                  <TextField
                    label="Nom"
                    value={newClient.username}
                    onChange={(e) => setNewClient({ ...newClient, username: e.target.value })}
                  />
                  <TextField
                    label="Email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  />
                  <TextField
                    label="Téléphone"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  />
                  <TextField
                    label="Adresse"
                    value={newClient.adresse}
                    onChange={(e) => setNewClient({ ...newClient, adresse: e.target.value })}
                  />
                  <Box mt={1}>
                    <IconButton color="primary" onClick={handleCreateClient}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </EditItemView>
  );
};

export default ProductPage;
